import React, { Fragment, useRef, useState } from 'react'
import Card from '../../../../../components/atoms/Card'
import Container from '../../../../../components/atoms/Container'
import Heading from '../../../../../components/atoms/Heading'
import Button from '../../../../../components/molecules/Button'
import { useTranslation } from '../../../../../providers/I18n'
import { BankCreditDetail } from '../../../../../types/credits'
import { StyledDescription } from '../../../../../components/styled/Description'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import {
  addCreditDocument,
  addCreditHistory,
  getCredit,
  updateCredit,
} from '../../../../../services/credits'
import { getCreditColor } from '../../../../../utils/credits'
import { useUser } from '../../../../../providers/User'
import CreditWaitAction from '../../../shared/CreditWaitAction'
import Textarea from '../../../../../components/atoms/Textarea'
import { CreditPhases, CreditSteps, Role } from '../../../../../types/enums'
import Spinner from '../../../../../components/atoms/Spinner'
import FileDropper, {
  FileWithBuffer,
} from '../../../../../components/molecules/FileDropper'
import { uploadDocuments } from '../../../../../utils/documents'
import { useAlert } from '../../../../../providers/Alert'

const Phase3SignatureReady: React.FC = () => {
  const commentRef = useRef<HTMLTextAreaElement>(null)
  const [firstEscrituraFile, setFirstEscrituraFile] = useState<
    FileWithBuffer[]
  >([])
  const [cobranzaFile, setCobranzaFile] = useState<FileWithBuffer[]>([])
  const { code } = useParams()
  const { user } = useUser()
  const { setAlert } = useAlert()
  const [loading, setLoading] = useState<boolean>(false)
  const { getTranslation } = useTranslation()
  const {
    data: credit,
    isLoading,
    refetch,
  } = useQuery<BankCreditDetail>(['getCredit', code], () => getCredit(code!), {
    refetchOnWindowFocus: false,
  })

  let header
  let footer
  let content
  if (!credit && isLoading) {
    content = (
      <Container>
        <StyledDescription>Cargando credito...</StyledDescription>
      </Container>
    )
  } else if (credit) {
    const isActiveOwner = user!.code === credit.owners.advisor?.code
    const color = getCreditColor(credit.phase)
    const creditPhaseStep = `${getTranslation!(
      `phases.${credit.phase}.title`
    )} - ${getTranslation!(`phases.${credit.phase}.steps.${credit.step}`)}`

    header = (
      <Container padding>
        <Heading type="3" color={color}>
          {creditPhaseStep}
        </Heading>
      </Container>
    )

    if (isActiveOwner || user?.role !== Role.Advisor) {
      const firstEscrituraBuffer = firstEscrituraFile
        ? firstEscrituraFile.map((fileWithBuffer) => {
            fileWithBuffer.fixedName = 'firstEscritura'
            return fileWithBuffer.file
          })
        : []

      const cobranzaBuffer = cobranzaFile
        ? cobranzaFile.map((fileWithBuffer) => {
            fileWithBuffer.fixedName = 'cobranza'
            return fileWithBuffer.file
          })
        : []

      const handleSendToSignatureDate = async () => {
        setLoading(true)

        const hasComment = !!commentRef.current?.value
        const hasEscritura = firstEscrituraFile.length > 0
        const hasDocumentos = cobranzaFile.length > 0
        if (!hasComment || !hasEscritura || !hasDocumentos) {
          setAlert!({
            severity: 'error',
            title: 'Error',
            description: 'Hacen falta campos requeridos',
          })
          setLoading(false)
        } else {
          await updateCredit(credit!.code, {
            phase: CreditPhases.Phase3,
            step: CreditSteps.Phase3Escriturado,
          }).then(async () => {
            const documents: FileWithBuffer[] = []
            if (firstEscrituraBuffer.length > 0) {
              documents.push(...firstEscrituraFile)
            }

            if (cobranzaBuffer.length > 0) {
              documents.push(...cobranzaFile)
            }

            const pushedCreditDocuments = await uploadDocuments(
              documents,
              `credit/${credit!.code.toString()}`
            )

            pushedCreditDocuments.forEach((document) => {
              addCreditDocument(credit.code, document!)
            })

            const comment = `Avanzando a Etapa 3 - Escriturado :: ${commentRef.current?.value}`
            const creditHistoryComment = {
              comment: comment,
              documents: pushedCreditDocuments,
              type: 'status',
              start: new Date(),
              change: {
                from: {
                  owner: credit.owners.advisor._id,
                  phase: credit.phase,
                  step: credit.step,
                },
                to: {
                  owner: credit.owners.phase3Operator._id,
                  phase: CreditPhases.Phase3,
                  step: CreditSteps.Phase3Escriturado,
                },
              },
            }
            await addCreditHistory(credit.code, creditHistoryComment).then(
              () => {
                if (commentRef.current) {
                  commentRef.current.value = ''
                }
                refetch().then(() => {
                  setLoading(false)
                })
              }
            )
          })
        }
      }

      const handleSendBack = async () => {
        setLoading(true)

        await updateCredit(credit!.code, {
          step: CreditSteps.Phase3SignatureKit,
        }).then(async () => {
          const comment = `Regresando a Etapa 3 - Kit de Firma :: ${commentRef.current?.value}`
          const creditHistoryComment = {
            comment: comment,
            type: 'status',
            start: new Date(),
            change: {
              from: {
                owner: credit.owners.advisor._id,
                step: credit.step,
              },
              to: {
                owner: credit.owners.phase3Operator._id,
                step: CreditSteps.Phase3SignatureKit,
              },
            },
          }

          await addCreditHistory(credit!.code, creditHistoryComment).then(
            () => {
              if (commentRef.current) {
                commentRef.current.value = ''
              }
              refetch().then(() => {
                setLoading(false)
              })
            }
          )
        })
      }

      content = (
        <Fragment>
          <StyledDescription>
            Listo para Firma, revisa Kit de Firma, confirma tu cita con cliente,
            notaria, banco.
          </StyledDescription>
          <StyledDescription>
            Favor de subir #1 hoja escritura y documentos de cobranza segun
            checklist una vez escriturado.
          </StyledDescription>
          <FileDropper
            label="Hoja #1 de Escritura"
            onChange={setFirstEscrituraFile}
            files={firstEscrituraBuffer}
          />
          <FileDropper
            label="Documentos de Cobranza"
            onChange={setCobranzaFile}
            files={cobranzaBuffer}
          />
          <Textarea label="Comentarios" ref={commentRef} required />
        </Fragment>
      )

      footer = (
        <Container align="right" direction="row" padding>
          {loading ? (
            <Spinner type="bar" />
          ) : (
            <>
              {user?.role !== Role.Advisor && (
                <Button
                  color={color}
                  priority="secondary"
                  onClick={handleSendBack}
                  loading={loading}
                >
                  Regresar a Kit de Firma
                </Button>
              )}
              <Button onClick={handleSendToSignatureDate} color={color}>
                Escriturado
              </Button>
            </>
          )}
        </Container>
      )
    } else {
      content = <CreditWaitAction owner={credit.owners.phase3Operator} />
    }
  }

  return (
    <div>
      <Card header={header} footer={footer}>
        {content}
      </Card>
    </div>
  )
}

export default Phase3SignatureReady
